import { Popover, Switch, Transition } from '@headlessui/react';
import { Fragment, useState } from 'react';
import {
	AdjustmentsHorizontalIcon,
	ArrowUpIcon,
	ArrowDownIcon,
	ArrowPathIcon,
	Bars3Icon,
	EyeIcon,
	EyeSlashIcon,
} from '@heroicons/react/20/solid';

/**
 * Panel para controlar visibilidad y orden de columnas de un table de TanStack.
 *
 * - `table`: instancia retornada por useReactTable.
 * - `onReset`: handler opcional para limpiar las preferencias de la tabla.
 *
 * Orden: flechas arriba/abajo o arrastrar desde el asa (HTML5 DnD).
 * Visibilidad: fila con switch; título e indicación cambian según estado (todas / ninguna / mixto).
 */
export const ColumnSettingsMenu = ({ table, onReset, disabled = false }) => {
	const [draggingId, setDraggingId] = useState(null);
	const [dragOverId, setDragOverId] = useState(null);

	const allColumns = table.getAllLeafColumns().filter((col) => col.columnDef?.meta?.sticky !== 'right');
	const orderedIds = (table.getState().columnOrder?.length
		? table.getState().columnOrder
		: allColumns.map((c) => c.id)
	).filter((id) => allColumns.some((c) => c.id === id));

	const ordered = orderedIds
		.map((id) => allColumns.find((c) => c.id === id))
		.filter(Boolean);

	const stickyRightIds = table
		.getAllLeafColumns()
		.filter((c) => c.columnDef?.meta?.sticky === 'right')
		.map((c) => c.id);

	const applyOrder = (reorderedIds) => {
		table.setColumnOrder([...reorderedIds, ...stickyRightIds]);
	};

	const move = (id, dir) => {
		const idx = ordered.findIndex((c) => c.id === id);
		const targetIdx = idx + dir;
		if (idx === -1 || targetIdx < 0 || targetIdx >= ordered.length) return;
		const next = ordered.map((c) => c.id);
		[next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
		applyOrder(next);
	};

	const reorderByDrag = (dragId, dropId) => {
		if (!dragId || dragId === dropId) return;
		const ids = ordered.map((c) => c.id);
		const from = ids.indexOf(dragId);
		const to = ids.indexOf(dropId);
		if (from === -1 || to === -1) return;
		const next = [...ids];
		const [removed] = next.splice(from, 1);
		next.splice(to, 0, removed);
		applyOrder(next);
	};

	const clearDragState = () => {
		setDraggingId(null);
		setDragOverId(null);
	};

	const allVisible =
		allColumns.length > 0 && allColumns.every((col) => col.getIsVisible());
	const visibleCount = allColumns.filter((col) => col.getIsVisible()).length;

	const bulkSwitchTitle = allVisible ? 'Ocultar todas' : 'Mostrar todas';
	const bulkSwitchHint = allVisible
		? 'Todas visibles. Al desactivar, ocultas todas las columnas de abajo; la fija a la derecha no cambia.'
		: visibleCount === 0
			? 'Ninguna visible. Al activar, muestras todas las columnas de la lista.'
			: `${visibleCount} de ${allColumns.length} visibles. Al activar, muestras todas a la vez.`;

	const setAllColumnsVisible = (show) => {
		const prev = table.getState().columnVisibility || {};
		const next = { ...prev };
		for (const col of allColumns) {
			next[col.id] = show;
		}
		table.setColumnVisibility(next);
	};

	return (
		<Popover className='relative flex w-full flex-col items-end md:inline-flex md:w-auto'>
			{({ open }) => (
				<>
					<Popover.Button
						disabled={disabled}
						className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${open ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'} ${disabled ? 'cursor-not-allowed opacity-50 hover:bg-white' : ''}`}
					>
						<AdjustmentsHorizontalIcon className='h-4 w-4' />
						<span>Columnas</span>
					</Popover.Button>

					<Transition
						as={Fragment}
						enter='transition ease-out duration-150'
						enterFrom='opacity-0 translate-y-1'
						enterTo='opacity-100 translate-y-0'
						leave='transition ease-in duration-100'
						leaveFrom='opacity-100 translate-y-0'
						leaveTo='opacity-0 translate-y-1'
					>
						<Popover.Panel className='absolute right-0 z-50 mt-2 w-[min(90vw,calc(100vw-1rem))] max-w-[min(90vw,calc(100vw-1rem))] origin-top-right overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-2xl shadow-slate-900/10 ring-1 ring-slate-900/5 md:w-96 md:max-w-[calc(100vw-2rem)]'>
							<div className='border-b border-slate-100 bg-gradient-to-b from-slate-50/70 to-white px-4 pb-3.5 pt-4'>
								<div className='flex items-center justify-between gap-3'>
									<h4 className='min-w-0 text-sm font-semibold leading-tight tracking-tight text-slate-900'>
										Configurar columnas
									</h4>
									{onReset && (
										<button
											type='button'
											onClick={onReset}
											className='inline-flex shrink-0 items-center gap-1 rounded-lg border border-slate-200/80 bg-white px-2 py-1 text-xs font-medium leading-none text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900'
										>
											<ArrowPathIcon className='h-3.5 w-3.5 shrink-0 text-slate-500' />
											Restablecer
										</button>
									)}
								</div>
								<p className='mt-3 text-[11px] leading-relaxed text-slate-500'>
									Orden con el asa o flechas. La columna fija a la derecha no se mueve aquí.
								</p>
							</div>

							{allColumns.length > 0 && (
								<div className='border-b border-slate-100 px-4 py-3'>
									<Switch.Group as='div' className='flex items-start justify-between gap-4'>
										<Switch.Label className='min-w-0 cursor-pointer'>
											<span className='block text-xs font-semibold leading-snug text-slate-900'>
												{bulkSwitchTitle}
											</span>
											<span className='mt-1 block text-[11px] leading-relaxed text-slate-500'>
												{bulkSwitchHint}
											</span>
										</Switch.Label>
										<Switch
											checked={allVisible}
											onChange={setAllColumnsVisible}
											className={`${
												allVisible ? 'bg-indigo-500' : 'bg-slate-200'
											} relative mt-0.5 inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full p-0.5 transition-colors duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/70 focus-visible:ring-offset-1 focus-visible:ring-offset-white`}
										>
											<span
												aria-hidden
												className={`pointer-events-none absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm ring-1 ring-slate-900/[0.05] transition-transform duration-200 ease-out ${
													allVisible ? 'translate-x-5' : 'translate-x-0'
												}`}
											/>
										</Switch>
									</Switch.Group>
								</div>
							)}

							<ul
								className={`max-h-[60vh] overflow-y-auto px-1.5 py-3 transition-[background-color] duration-500 ease-out ${
									draggingId ? 'rounded-b-xl bg-slate-50/90' : ''
								}`}
							>
								{ordered.map((column, index) => {
									const isVisible = column.getIsVisible();
									const headerLabel = renderableHeaderText(column);
									const isDragging = draggingId === column.id;
									const isOver = dragOverId === column.id && draggingId && draggingId !== column.id;

									return (
										<li
											key={column.id}
											data-dragging={isDragging || undefined}
											data-drop-target={isOver || undefined}
											onDragOver={(e) => {
												e.preventDefault();
												e.dataTransfer.dropEffect = 'move';
												if (draggingId && draggingId !== column.id) {
													setDragOverId(column.id);
												}
											}}
											onDragLeave={(e) => {
												if (!e.currentTarget.contains(e.relatedTarget)) {
													setDragOverId((prev) => (prev === column.id ? null : prev));
												}
											}}
											onDrop={(e) => {
												e.preventDefault();
												const dragId = e.dataTransfer.getData('text/plain') || draggingId;
												reorderByDrag(dragId, column.id);
												clearDragState();
											}}
											className={[
												'relative mx-0.5 my-1 flex items-center gap-2 rounded-2xl px-2 py-1.5',
												'transition-[background-color,box-shadow,opacity,transform] duration-200 ease-out',
												draggingId ? '' : 'hover:bg-white/70',
												isDragging
													? 'z-20 border border-slate-200/50 bg-slate-100/40 opacity-50 shadow-inner'
													: '',
												isOver
													? 'z-10 scale-[1.02] bg-slate-200/90 shadow-md shadow-slate-900/12 ring-1 ring-slate-300/70'
													: '',
												draggingId && !isDragging && !isOver ? 'opacity-60' : '',
											]
												.filter(Boolean)
												.join(' ')}
										>
											<div
												draggable
												onDragStart={(e) => {
													const li = e.currentTarget.closest('li');
													if (li && typeof e.dataTransfer.setDragImage === 'function') {
														const rect = li.getBoundingClientRect();
														const ox = Math.max(0, e.clientX - rect.left);
														const oy = Math.max(0, e.clientY - rect.top);
														e.dataTransfer.setDragImage(li, ox, oy);
													}
													e.dataTransfer.setData('text/plain', column.id);
													e.dataTransfer.effectAllowed = 'move';
													setDraggingId(column.id);
													setDragOverId(null);
												}}
												onDragEnd={clearDragState}
												className={[
													'inline-flex h-9 w-9 shrink-0 touch-none items-center justify-center rounded-md text-slate-500',
													'cursor-grab bg-slate-100/80 transition-colors duration-200 ease-out',
													'hover:bg-slate-50 hover:text-slate-800 active:cursor-grabbing',
													isDragging ? 'cursor-grabbing bg-slate-200 text-slate-800' : '',
												]
													.filter(Boolean)
													.join(' ')}
												title='Arrastrar para reordenar'
												aria-label='Arrastrar para reordenar columna'
												aria-grabbed={isDragging}
											>
												<Bars3Icon className='h-4 w-4' aria-hidden />
											</div>

											<button
												type='button'
												onClick={() => column.toggleVisibility(!isVisible)}
												className='inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700'
												aria-label={isVisible ? 'Ocultar columna' : 'Mostrar columna'}
											>
												{isVisible ? (
													<EyeIcon className='h-4 w-4 text-indigo-600' />
												) : (
													<EyeSlashIcon className='h-4 w-4' />
												)}
											</button>

											<span className={`min-w-0 flex-1 truncate text-sm ${isVisible ? 'text-gray-800' : 'text-gray-400 line-through'}`}>
												{headerLabel || column.id}
											</span>

											<div className='inline-flex shrink-0 items-center gap-0.5'>
												<button
													type='button'
													onClick={() => move(column.id, -1)}
													disabled={index === 0}
													aria-label='Mover arriba'
													className='inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-30'
												>
													<ArrowUpIcon className='h-3.5 w-3.5' />
												</button>
												<button
													type='button'
													onClick={() => move(column.id, +1)}
													disabled={index === ordered.length - 1}
													aria-label='Mover abajo'
													className='inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-30'
												>
													<ArrowDownIcon className='h-3.5 w-3.5' />
												</button>
											</div>
										</li>
									);
								})}
							</ul>
						</Popover.Panel>
					</Transition>
				</>
			)}
		</Popover>
	);
};

const renderableHeaderText = (column) => {
	const meta = column.columnDef?.meta;
	if (meta?.label) return meta.label;
	const header = column.columnDef?.header;
	if (typeof header === 'string') return header;
	return '';
};
